import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import ImageUpload from './ImageUpload';
import { getAuthToken } from '../utils/auth';

const HotelLibrary = () => {
  const [hotels, setHotels] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    destination: '',
    city: '',
    country: '',
    category: '3-star',
    address: '',
    description: '',
    amenities: '',
    roomTypes: '',
    pricePerNight: '',
    phone: '',
    email: '',
    website: '',
    imageUrl: '',
    isActive: true
  });

  useEffect(() => {
    fetchHotels();
    fetchDestinations();
  }, []);

  const fetchHotels = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get('/master-data/hotels', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHotels(response.data.hotels || response.data);
    } catch (error) {
      console.error('Error fetching hotels:', error);
    }
  };

  const fetchDestinations = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get('/master-data/destinations-manage', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDestinations(response.data.destinations || response.data);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      const dataToSend = {
        name: formData.name,
        destination: formData.destination,
        city: formData.city,
        country: formData.country,
        category: formData.category,
        address: formData.address,
        description: formData.description,
        amenities: formData.amenities.split(',').map(a => a.trim()).filter(a => a),
        roomTypes: formData.roomTypes.split(',').map(r => r.trim()).filter(r => r),
        pricePerNight: {
          standard: parseFloat(formData.pricePerNight) || 0
        },
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        images: formData.imageUrl ? [{ url: formData.imageUrl }] : [],
        isActive: formData.isActive
      };

      if (editingId) {
        await axios.put(`/master-data/hotels/${editingId}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/master-data/hotels', dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      fetchHotels();
      resetForm();
    } catch (error) {
      console.error('Error saving hotel:', error);
      alert('Error saving hotel: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (hotel) => {
    setFormData({
      name: hotel.name || '',
      destination: hotel.destination || '',
      city: hotel.city || '',
      country: hotel.country || '',
      category: hotel.category || '3-star',
      address: hotel.address || '',
      description: hotel.description || '',
      amenities: hotel.amenities?.join(', ') || '',
      roomTypes: hotel.roomTypes?.join(', ') || '',
      pricePerNight: hotel.pricePerNight?.standard || '',
      phone: hotel.phone || '',
      email: hotel.email || '',
      website: hotel.website || '',
      imageUrl: hotel.images?.[0]?.url || '',
      isActive: hotel.isActive !== false
    });
    setEditingId(hotel._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this hotel?')) {
      try {
        const token = getAuthToken();
        await axios.delete(`/master-data/hotels/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchHotels();
      } catch (error) {
        console.error('Error deleting hotel:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      destination: '',
      city: '',
      country: '',
      category: '3-star',
      address: '',
      description: '',
      amenities: '',
      roomTypes: '',
      pricePerNight: '',
      phone: '',
      email: '',
      website: '',
      imageUrl: '',
      isActive: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Hotels Library</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : '+ Add Hotel'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? 'Edit Hotel' : 'Add New Hotel'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Hotel Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Destination *</label>
              <select
                required
                value={formData.destination}
                onChange={(e) => {
                  const selectedDest = destinations.find(d => d.name === e.target.value);
                  if (selectedDest) {
                    setFormData({ 
                      ...formData, 
                      destination: selectedDest.name,
                      city: selectedDest.name,
                      country: selectedDest.country
                    });
                  }
                }}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">Select Destination</option>
                {destinations.map((dest) => (
                  <option key={dest._id} value={dest.name}>
                    {dest.name}, {dest.country}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category *</label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full border rounded px-3 py-2"
              >
                <option value="2-star">2-Star</option>
                <option value="3-star">3-Star</option>
                <option value="3-star-superior">3-Star Superior</option>
                <option value="4-star">4-Star</option>
                <option value="5-star">5-Star</option>
                <option value="Resort">Resort</option>
                <option value="Boutique">Boutique</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Price Per Night *</label>
              <input
                type="number"
                required
                value={formData.pricePerNight}
                onChange={(e) => setFormData({ ...formData, pricePerNight: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border rounded px-3 py-2"
                rows="2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Amenities (comma-separated)</label>
              <input
                type="text"
                value={formData.amenities}
                onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="WiFi, Pool, Gym"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Room Types (comma-separated)</label>
              <input
                type="text"
                value={formData.roomTypes}
                onChange={(e) => setFormData({ ...formData, roomTypes: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="Deluxe, Suite, Standard"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Hotel Image</label>
              <ImageUpload
                currentImage={formData.imageUrl}
                onUploadSuccess={(url) => setFormData({ ...formData, imageUrl: url })}
                folder="tours-travels/hotels"
              />
            </div>
            <div className="col-span-2 flex gap-2">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                {editingId ? 'Update' : 'Save'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {hotels.map((hotel) => (
          <div key={hotel._id} className="bg-white rounded-lg shadow p-4">
            {hotel.images?.[0]?.url && (
              <img
                src={hotel.images[0].url}
                alt={hotel.name}
                className="w-full h-40 object-cover rounded mb-3"
              />
            )}
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold text-lg">{hotel.name}</h3>
              <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                {hotel.category}
              </span>
            </div>
            <p className="text-gray-600 text-sm mb-2">
              📍 {hotel.city}, {hotel.country}
            </p>
            <p className="text-gray-700 text-sm mb-3">{hotel.description}</p>
            <div className="flex flex-wrap gap-1 mb-3">
              {hotel.amenities.slice(0, 3).map((amenity, idx) => (
                <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                  {amenity}
                </span>
              ))}
            </div>
            <p className="font-semibold text-lg text-blue-600 mb-3">
              ₹{hotel.pricePerNight?.standard || 0}/night
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(hotel)}
                className="flex-1 bg-blue-50 text-blue-600 px-3 py-1 rounded text-sm hover:bg-blue-100"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(hotel._id)}
                className="flex-1 bg-red-50 text-red-600 px-3 py-1 rounded text-sm hover:bg-red-100"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HotelLibrary;
